import { useState } from 'react';
import { AlertTriangle, Trophy, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { useLostReasons, useMoveDeal, useSaveDeal } from './api';
import { missingFields } from './deals-board';
import type { Deal, PipelineStage } from './types';

const FIELD_LABELS: Record<string, string> = {
  value: 'Deal value',
  expectedCloseDate: 'Expected close date',
  contactId: 'Primary contact',
  ownerId: 'Owner',
  probability: 'Probability',
  source: 'Source',
};

/**
 * Handles the three moves that need more than a drag: leaving a stage with
 * required fields unset (spec PIPE-06), winning (PIPE-07) and losing (PIPE-08).
 */
export function MoveDealDialog({
  deal,
  targetStage,
  currentStage,
  onClose,
}: {
  deal: Deal;
  targetStage: PipelineStage;
  currentStage?: PipelineStage;
  onClose: () => void;
}) {
  const move = useMoveDeal();
  const saveDeal = useSaveDeal();
  const { data: lostReasons = [] } = useLostReasons();
  const [error, setError] = useState<string | null>(null);

  const missing = missingFields(deal, currentStage);

  const [fill, setFill] = useState({
    value: deal.value ?? '',
    expectedCloseDate: deal.expectedCloseDate?.slice(0, 10) ?? '',
    probability: deal.probability?.toString() ?? '',
  });
  const [actualValue, setActualValue] = useState(deal.value ?? '');
  const [actualCloseDate, setActualCloseDate] = useState(new Date().toISOString().slice(0, 10));
  const [lostReason, setLostReason] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [lostNote, setLostNote] = useState('');

  const won = targetStage.stageType === 'WON';
  const lost = targetStage.stageType === 'LOST';

  // Fields the dialog cannot collect inline need the record page instead.
  const uncollectable = missing.filter(
    (field) => !['value', 'expectedCloseDate', 'probability'].includes(field),
  );

  const submit = async () => {
    setError(null);
    try {
      // Fill the gaps first, so the move itself is a clean transition.
      if (missing.length > 0 && uncollectable.length === 0) {
        await saveDeal.mutateAsync({
          id: deal.id,
          ...(missing.includes('value') ? { value: fill.value } : {}),
          ...(missing.includes('expectedCloseDate')
            ? { expectedCloseDate: fill.expectedCloseDate }
            : {}),
          ...(missing.includes('probability') ? { probability: Number(fill.probability) } : {}),
        });
      }

      await move.mutateAsync({
        id: deal.id,
        stageId: targetStage.id,
        ...(won ? { actualValue, actualCloseDate } : {}),
        ...(lost ? { lostReason, competitor, lostNote } : {}),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not move the deal.');
    }
  };

  const blocked = uncollectable.length > 0;
  const canSubmit =
    !blocked &&
    (!lost || lostReason.length > 0) &&
    (!missing.includes('value') || fill.value.length > 0) &&
    (!missing.includes('expectedCloseDate') || fill.expectedCloseDate.length > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {won && <Trophy className="h-5 w-5 text-emerald-600" />}
            {lost && <XCircle className="h-5 w-5 text-destructive" />}
            {won ? 'Mark as won' : lost ? 'Mark as lost' : `Move to ${targetStage.name}`}
          </DialogTitle>
          <DialogDescription>
            {deal.title} · {deal.company.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {missing.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  &ldquo;{currentStage?.name}&rdquo; requires{' '}
                  {missing.map((field) => FIELD_LABELS[field] ?? field).join(', ')} before a deal can
                  leave it.
                </span>
              </p>

              {blocked ? (
                <p className="mt-2 text-xs text-amber-800">
                  Set {uncollectable.map((f) => FIELD_LABELS[f] ?? f).join(' and ')} on the deal
                  first — they cannot be filled in here.
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {missing.includes('value') && (
                    <div className="space-y-1.5">
                      <Label>Deal value</Label>
                      <Input
                        inputMode="decimal"
                        placeholder="0.00"
                        value={fill.value}
                        onChange={(event) => setFill({ ...fill, value: event.target.value })}
                      />
                    </div>
                  )}
                  {missing.includes('expectedCloseDate') && (
                    <div className="space-y-1.5">
                      <Label>Expected close date</Label>
                      <Input
                        type="date"
                        value={fill.expectedCloseDate}
                        onChange={(event) =>
                          setFill({ ...fill, expectedCloseDate: event.target.value })
                        }
                      />
                    </div>
                  )}
                  {missing.includes('probability') && (
                    <div className="space-y-1.5">
                      <Label>Probability %</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={fill.probability}
                        onChange={(event) => setFill({ ...fill, probability: event.target.value })}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {won && (
            <>
              <div className="space-y-1.5">
                <Label>Value actually agreed</Label>
                <Input
                  inputMode="decimal"
                  value={actualValue}
                  onChange={(event) => setActualValue(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  This replaces the forecast value on the deal.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Close date</Label>
                <Input
                  type="date"
                  value={actualCloseDate}
                  onChange={(event) => setActualCloseDate(event.target.value)}
                />
              </div>
            </>
          )}

          {lost && (
            <>
              <div className="space-y-1.5">
                <Label>Why was it lost?</Label>
                <Select value={lostReason} onChange={(event) => setLostReason(event.target.value)}>
                  <option value="">Choose a reason</option>
                  {lostReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Competitor</Label>
                <Input
                  value={competitor}
                  placeholder="Optional"
                  onChange={(event) => setCompetitor(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  value={lostNote}
                  placeholder="Anything worth knowing next time"
                  onChange={(event) => setLostNote(event.target.value)}
                />
              </div>
            </>
          )}

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || move.isPending || saveDeal.isPending}
            variant={lost ? 'destructive' : 'default'}
          >
            {move.isPending || saveDeal.isPending
              ? 'Saving…'
              : won
                ? 'Mark as won'
                : lost
                  ? 'Mark as lost'
                  : `Move to ${targetStage.name}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
